import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, Check, X } from 'lucide-react';
import { useNotifications, type Notification } from '@/lib/contexts/NotificationContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRouter } from 'next/navigation';

export function NotificationButton() {
  const { notifications, unreadCount, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] max-h-[85vh] overflow-hidden p-0 bg-background/95 backdrop-blur-sm border shadow-lg"
      >
        <div className="flex items-center justify-between border-b px-4 py-3 bg-background/95">
          <h2 className="font-semibold">Notifications</h2>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-sm"
              onClick={markAllAsRead}
            >
              <Check className="mr-2 h-4 w-4" />
              Mark all as read
            </Button>
          )}
        </div>
        <div className="overflow-y-auto max-h-[calc(85vh-60px)]">
          <AnimatePresence initial={false}>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClose={() => setOpen(false)}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground bg-background/95">
                <Bell className="h-12 w-12 mb-4 opacity-20" />
                <p>No notifications yet</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationItem({ notification, onClose }: { notification: Notification; onClose: () => void }) {
  const { markAsRead, removeNotification, setIssueToShow } = useNotifications();
  const [isHovered, setIsHovered] = useState(false);
  const router = useRouter();

  const getTypeStyles = (type: Notification['type']) => {
    switch (type) {
      case 'mention':
        return 'bg-blue-500/10 text-blue-500';
      case 'issue':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'system':
        return 'bg-purple-500/10 text-purple-500';
      case 'update':
        return 'bg-green-500/10 text-green-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const handleClick = () => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.type === 'issue') {
      const issueId = notification.issueId || notification.link?.split('/').pop();
      if (issueId) {
        setIssueToShow(issueId);
        router.push('/issues');
      }
    } else if (notification.link) {
      router.push(notification.link);
    }

    onClose();
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'relative flex items-start gap-4 px-4 py-3 border-b transition-colors bg-background/95',
        !notification.read && 'bg-muted/50',
        isHovered && 'bg-muted'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div
        className={cn(
          'mt-1 h-2 w-2 rounded-full',
          getTypeStyles(notification.type)
        )}
      />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium leading-none">
          {notification.title}
        </p>
        <p className="text-sm text-muted-foreground">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(notification.timestamp).toLocaleDateString()} at{' '}
          {new Date(notification.timestamp).toLocaleTimeString()}
        </p>
      </div>
      {isHovered && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            removeNotification(notification.id);
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </motion.div>
  );
} 